// Entry addresses of the applications served by this platform: agenda.vename.com and
// contacts.vename.com redirect to the webmail with ?vue=agenda or ?vue=contacts. The requested
// application is kept in the session storage so that it survives the sign-in round trip.
export type EntryView = 'calendar' | 'contacts';

const views: Record<string, EntryView> = { agenda: 'calendar', contacts: 'contacts' };
const key = 'vename.entry-view';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The session storage, or nothing when the browser blocks it (reading the global throws then). */
export function sessionStore(): Store | undefined {
  try {
    return sessionStorage;
  } catch {
    return undefined;
  }
}

export function entryViewFromSearch(search: string): EntryView | undefined {
  const name = new URLSearchParams(search).get('vue') ?? '';
  return Object.hasOwn(views, name) ? views[name] : undefined;
}

/** Remembers the application named in the address and removes it from the address bar. */
export function rememberEntryView(
  location: Pick<Location, 'search' | 'pathname' | 'hash'>,
  history: Pick<History, 'replaceState'>,
  store: Store | undefined,
) {
  const view = entryViewFromSearch(location.search);
  if (!view) return;
  try {
    store?.setItem(key, view);
  } catch {
    // Without storage the application opens on the messaging view, as before.
  }
  const search = new URLSearchParams(location.search);
  search.delete('vue');
  const rest = search.toString();
  history.replaceState(null, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
}

/** Returns the remembered application once, then forgets it. */
export function takeEntryView(store: Store | undefined): EntryView | undefined {
  if (!store) return undefined;
  try {
    const value = store.getItem(key) ?? '';
    store.removeItem(key);
    return (Object.values(views) as string[]).includes(value) ? (value as EntryView) : undefined;
  } catch {
    return undefined;
  }
}

// After the sign-in that confirms a sensitive action, the settings tab the user came from.
export type ReturnTab = 'security' | 'data';
const returnKey = 'vename.return-tab';

export function rememberReturnTab(tab: ReturnTab, store: Store | undefined) {
  try {
    store?.setItem(returnKey, tab);
  } catch {
    // Without storage the application opens on the messaging view after the sign-in.
  }
}

/** Returns the remembered settings tab once, then forgets it. */
export function takeReturnTab(store: Store | undefined): ReturnTab | undefined {
  if (!store) return undefined;
  try {
    const value = store.getItem(returnKey) ?? '';
    store.removeItem(returnKey);
    return value === 'security' || value === 'data' ? value : undefined;
  } catch {
    return undefined;
  }
}
